/**
 * Widget Settings Page
 *
 * Dedicated page for configuring the chat widget appearance, previewing it
 * live, and grabbing integration code. Replaces the cramped dialog that
 * previously lived inside DeployPage.
 *
 * Layout: split-pane — scrollable settings on the left, sticky live preview
 * + integration code on the right.
 */

import React, { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAgentContext } from '../../../components/layout/AgentContextLayout'
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/Card'
import { Button } from '../../../components/ui/Button'
import { Input } from '../../../components/ui/Input'
import { NativeSelect } from '../../../components/ui/NativeSelect'
import { Textarea } from '../../../components/ui/Textarea'
import { Switch } from '../../../components/ui/switch'
import { Modal } from '../../../components/ui/Modal'
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from '../../../components/ui/collapsible'
import {
  ArrowLeft, Save, Copy, Check, ChevronDown, ChevronRight,
  Palette, Type, Image, Settings, Code, Eye,
  Loader2, HelpCircle, Sparkles, ExternalLink,
} from 'lucide-react'

import { chatbotsService } from '../../../services/chatbots'
import { logger } from '../../../utils/logger'
import { cn } from '../../../utils/cn'
import { API_BASE_URL } from '../../../utils/constants'

// ────────────────────────────────────────────
// Types
// ────────────────────────────────────────────

interface WidgetConfig {
  chatbotId: string
  apiUrl: string
  position: 'bottom-right' | 'bottom-left'
  primaryColor: string
  widgetTitle: string
  greeting: string
  placeholder: string
  theme: 'light' | 'dark'
  zIndex: number
  borderRadius: number
  fontSize: number
  fontWeight: number
  fontFamily: string
  widgetWidth: number
  widgetHeight: number
  shadowIntensity: 'none' | 'light' | 'medium' | 'strong'
  animationSpeed: 'slow' | 'normal' | 'fast'
  messageStyle: 'rounded' | 'sharp' | 'bubble'
  showBranding: boolean
  customCSS: string
  userMessageBg: string
  userMessageText: string
  botMessageBg: string
  botMessageText: string
  agentMessageBg: string
  agentMessageText: string
  widgetIcon: string
  iconType: 'upload' | 'emoji' | 'library'
  brandingUrl: string
  sessionMode: 'continuous' | 'per_page'
}

// ────────────────────────────────────────────
// Helpers
// ────────────────────────────────────────────

const getAuthToken = (): string | null => {
  try {
    const storedState = localStorage.getItem('bgai-auth-storage')
    const directToken = localStorage.getItem('token')
    if (storedState) {
      const parsed = JSON.parse(storedState)
      if (parsed.state?.token) return parsed.state.token
    }
    if (directToken) return directToken
  } catch {
    // ignore
  }
  return null
}

const DEFAULT_CONFIG: Omit<WidgetConfig, 'chatbotId' | 'apiUrl'> = {
  position: 'bottom-right',
  primaryColor: '#3b82f6',
  widgetTitle: 'Chat with us',
  greeting: 'Hello! How can I help you today?',
  placeholder: 'Type your message...',
  theme: 'light',
  zIndex: 999999,
  borderRadius: 12,
  fontSize: 14,
  fontWeight: 400,
  fontFamily: 'Inter, system-ui, sans-serif',
  widgetWidth: 440,
  widgetHeight: 720,
  shadowIntensity: 'medium',
  animationSpeed: 'normal',
  messageStyle: 'rounded',
  showBranding: true,
  customCSS: '',
  userMessageBg: '#3b82f6',
  userMessageText: '#ffffff',
  botMessageBg: '#ffffff',
  botMessageText: '#374151',
  agentMessageBg: '#ecfdf5',
  agentMessageText: '#065f46',
  widgetIcon: '',
  iconType: 'emoji',
  brandingUrl: '',
  sessionMode: 'continuous',
}

// ────────────────────────────────────────────
// Collapsible Section component
// ────────────────────────────────────────────

interface SectionProps {
  title: string
  icon: React.ReactNode
  defaultOpen?: boolean
  children: React.ReactNode
}

const Section: React.FC<SectionProps> = ({ title, icon, defaultOpen = true, children }) => {
  const [open, setOpen] = useState(defaultOpen)
  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <Card>
        <CollapsibleTrigger asChild>
          <button className="flex items-center justify-between w-full px-4 py-3 text-left hover:bg-muted/50 transition-colors rounded-t-lg">
            <div className="flex items-center gap-2">
              {icon}
              <span className="text-sm font-semibold">{title}</span>
            </div>
            {open ? <ChevronDown className="h-4 w-4 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 text-muted-foreground" />}
          </button>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <CardContent className="pt-0 pb-4 px-4">
            {children}
          </CardContent>
        </CollapsibleContent>
      </Card>
    </Collapsible>
  )
}

// ────────────────────────────────────────────
// Color picker row
// ────────────────────────────────────────────

const ColorField: React.FC<{ label: string; value: string; onChange: (v: string) => void }> = ({ label, value, onChange }) => (
  <div>
    <label className="block text-sm font-medium text-muted-foreground mb-1.5">{label}</label>
    <div className="flex gap-2">
      <Input
        type="color"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-12 h-9 p-0.5 border rounded cursor-pointer"
      />
      <Input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="flex-1 font-mono text-xs"
      />
    </div>
  </div>
)

// ────────────────────────────────────────────
// Main Page Component
// ────────────────────────────────────────────

export const WidgetSettingsPage: React.FC = () => {
  const { chatbot } = useAgentContext()
  const navigate = useNavigate()
  const chatbotId = chatbot.id

  // State
  const [config, setConfig] = useState<WidgetConfig>({
    chatbotId,
    apiUrl: API_BASE_URL,
    ...DEFAULT_CONFIG,
  })
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saved' | 'error'>('idle')
  const [showCSSGuide, setShowCSSGuide] = useState(false)
  const [showCode, setShowCode] = useState(false)
  const [copied, setCopied] = useState(false)

  // ── Fetch existing appearance ──

  useEffect(() => {
    fetchAppearance()
  }, [chatbotId])

  const fetchAppearance = async () => {
    try {
      setIsLoading(true)
      const token = getAuthToken()
      const headers: Record<string, string> = {}
      if (token) headers['Authorization'] = `Bearer ${token}`

      const res = await fetch(`${API_BASE_URL}/widget-appearance/chatbot/${chatbotId}`, { headers })
      if (res.ok) {
        const a = await res.json()
        setConfig(prev => ({
          ...prev,
          widgetTitle: a.widget_title || DEFAULT_CONFIG.widgetTitle,
          greeting: a.greeting_message || `Hello! I'm ${chatbot.name}. How can I help you today?`,
          primaryColor: a.primary_color || DEFAULT_CONFIG.primaryColor,
          placeholder: a.placeholder_text || DEFAULT_CONFIG.placeholder,
          theme: a.theme || DEFAULT_CONFIG.theme,
          position: a.widget_position || DEFAULT_CONFIG.position,
          borderRadius: a.border_radius ?? DEFAULT_CONFIG.borderRadius,
          fontSize: a.font_size ?? DEFAULT_CONFIG.fontSize,
          fontWeight: a.font_weight ?? DEFAULT_CONFIG.fontWeight,
          fontFamily: a.font_family || DEFAULT_CONFIG.fontFamily,
          widgetWidth: a.widget_width ?? DEFAULT_CONFIG.widgetWidth,
          widgetHeight: a.widget_height ?? DEFAULT_CONFIG.widgetHeight,
          shadowIntensity: a.shadow_intensity || DEFAULT_CONFIG.shadowIntensity,
          animationSpeed: a.animation_speed || DEFAULT_CONFIG.animationSpeed,
          messageStyle: a.message_style || DEFAULT_CONFIG.messageStyle,
          showBranding: a.show_branding !== false,
          customCSS: a.custom_css || '',
          zIndex: a.z_index ?? DEFAULT_CONFIG.zIndex,
          userMessageBg: a.user_message_bg || DEFAULT_CONFIG.userMessageBg,
          userMessageText: a.user_message_text || DEFAULT_CONFIG.userMessageText,
          botMessageBg: a.bot_message_bg || DEFAULT_CONFIG.botMessageBg,
          botMessageText: a.bot_message_text || DEFAULT_CONFIG.botMessageText,
          agentMessageBg: a.agent_message_bg || DEFAULT_CONFIG.agentMessageBg,
          agentMessageText: a.agent_message_text || DEFAULT_CONFIG.agentMessageText,
          widgetIcon: a.widget_icon || '',
          iconType: a.widget_icon
            ? (a.widget_icon.startsWith('data:image') ? 'upload'
              : a.widget_icon.includes('/static/avatars/peep-') || a.widget_icon.startsWith('<svg') || a.widget_icon.includes('svg') ? 'library' : 'emoji')
            : 'emoji',
          brandingUrl: a.branding_url || '',
          sessionMode: a.session_mode || DEFAULT_CONFIG.sessionMode,
        }))
      } else {
        // No saved appearance — use defaults with chatbot name
        setConfig(prev => ({
          ...prev,
          widgetTitle: chatbot.name || DEFAULT_CONFIG.widgetTitle,
          greeting: `Hello! I'm ${chatbot.name}. How can I help you today?`,
        }))
      }
    } catch (error) {
      logger.error('Failed to load widget appearance', {
        component: 'WidgetSettingsPage',
        error: error instanceof Error ? error.message : 'Unknown error',
      })
    } finally {
      setIsLoading(false)
    }
  }

  // ── Config change handler ──

  const handleChange = useCallback((field: keyof WidgetConfig, value: any) => {
    setConfig(prev => ({ ...prev, [field]: value }))
  }, [])

  // ── Save ──

  const saveWidgetSettings = async () => {
    try {
      setIsSaving(true)
      setSaveStatus('idle')

      const token = getAuthToken()
      if (!token) throw new Error('No authentication token found')

      const appearanceData = {
        widget_title: config.widgetTitle,
        greeting_message: config.greeting,
        placeholder_text: config.placeholder,
        primary_color: config.primaryColor,
        widget_position: config.position,
        theme: config.theme,
        border_radius: config.borderRadius,
        font_size: config.fontSize,
        font_family: config.fontFamily,
        shadow_intensity: config.shadowIntensity,
        animation_speed: config.animationSpeed,
        message_style: config.messageStyle,
        show_branding: config.showBranding,
        custom_css: config.customCSS,
        z_index: config.zIndex,
        user_message_bg: config.userMessageBg,
        user_message_text: config.userMessageText,
        bot_message_bg: config.botMessageBg,
        bot_message_text: config.botMessageText,
        agent_message_bg: config.agentMessageBg,
        agent_message_text: config.agentMessageText,
        widget_icon: config.widgetIcon,
      }

      const res = await fetch(`${API_BASE_URL}/widget-appearance/chatbot/${chatbotId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(appearanceData),
      })
      if (!res.ok) throw new Error(`Failed to save appearance: ${res.status}`)

      // Save extra settings to chatbot settings JSON
      try {
        const currentSettings = (chatbot as any).settings || {}
        await chatbotsService.updateChatbot(chatbotId, {
          settings: {
            ...currentSettings,
            widget_width: config.widgetWidth,
            widget_height: config.widgetHeight,
            font_weight: config.fontWeight,
            session_mode: config.sessionMode,
          },
        } as any)
      } catch (e) {
        logger.warn('Failed to save widget size to chatbot settings', {
          component: 'WidgetSettingsPage',
          error: e instanceof Error ? e.message : 'Unknown error',
        })
      }

      setSaveStatus('saved')
      setTimeout(() => setSaveStatus('idle'), 2500)
    } catch (error) {
      logger.error('Failed to save widget settings', {
        component: 'WidgetSettingsPage',
        error: error instanceof Error ? error.message : 'Unknown error',
      })
      setSaveStatus('error')
      setTimeout(() => setSaveStatus('idle'), 3000)
    } finally {
      setIsSaving(false)
    }
  }

  // ── Embed code ──

  const generateEmbedCode = () => {
    const timestamp = Date.now()
    const variables = (chatbot as any)?.settings?.variables || {}
    const varNames = Object.keys(variables)

    if (varNames.length === 0) {
      return `<!-- CleverAct AI Chat Widget -->\n<script src="${config.apiUrl}/static/premium-widget-v4.js?v=${timestamp}"></script>\n<script>\n  BGAIPremiumWidget.init({\n    chatbotId: '${config.chatbotId}',\n    apiUrl: '${config.apiUrl}'\n  });\n</script>`
    }

    const varLines = varNames.map(name => {
      const cfg = variables[name]
      const comment = cfg?.required ? ' // required' : ''
      return `      ${name}: 'YOUR_${name.toUpperCase()}'${comment}`
    }).join(',\n')

    return `<!-- CleverAct AI Chat Widget -->\n<script src="${config.apiUrl}/static/premium-widget-v4.js?v=${timestamp}"></script>\n<script>\n  BGAIPremiumWidget.init({\n    chatbotId: '${config.chatbotId}',\n    apiUrl: '${config.apiUrl}',\n    variables: {\n${varLines}\n    }\n  });\n</script>`
  }

  const copyEmbedCode = async () => {
    try {
      await navigator.clipboard.writeText(generateEmbedCode())
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      // ignore
    }
  }

  // ── Loading state ──

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    )
  }

  // ── Render ──

  return (
    <div className="flex flex-col h-full">
      {/* ── Header ── */}
      <div className="flex items-center justify-between gap-4 pb-4 border-b mb-6">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="gap-1.5">
            <ArrowLeft className="h-4 w-4" />
            Back
          </Button>
          <div>
            <h1 className="text-lg font-semibold">Chat Widget Settings</h1>
            <p className="text-sm text-muted-foreground">Customize the look and feel of your embedded chat widget</p>
          </div>
        </div>
        <Button onClick={saveWidgetSettings} disabled={isSaving} className="gap-1.5">
          {isSaving ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : saveStatus === 'saved' ? (
            <Check className="h-4 w-4" />
          ) : (
            <Save className="h-4 w-4" />
          )}
          {isSaving ? 'Saving...' : saveStatus === 'saved' ? 'Saved!' : saveStatus === 'error' ? 'Failed – Retry' : 'Save'}
        </Button>
      </div>

      {/* ── Split layout ── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 flex-1 min-h-0">

        {/* ── LEFT: Settings ── */}
        <div className="space-y-3 overflow-y-auto pb-8 pr-1">

          {/* General */}
          <Section title="General" icon={<Settings className="h-4 w-4 text-muted-foreground" />}>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="col-span-2 sm:col-span-1">
                <label className="block text-sm font-medium text-muted-foreground mb-1.5">Widget Title</label>
                <Input value={config.widgetTitle} onChange={e => handleChange('widgetTitle', e.target.value)} placeholder="Chat with us" />
              </div>
              <div>
                <label className="block text-sm font-medium text-muted-foreground mb-1.5">Position</label>
                <NativeSelect value={config.position} onValueChange={v => handleChange('position', v)}>
                  <option value="bottom-right">Bottom Right</option>
                  <option value="bottom-left">Bottom Left</option>
                </NativeSelect>
              </div>
              <div>
                <label className="block text-sm font-medium text-muted-foreground mb-1.5">Theme</label>
                <NativeSelect value={config.theme} onValueChange={v => handleChange('theme', v)}>
                  <option value="light">Light</option>
                  <option value="dark">Dark</option>
                </NativeSelect>
              </div>
              <div className="col-span-2">
                <label className="block text-sm font-medium text-muted-foreground mb-1.5">Greeting Message</label>
                <Textarea value={config.greeting} onChange={e => handleChange('greeting', e.target.value)} placeholder="Hello! How can I help you today?" rows={2} />
              </div>
              <div className="col-span-2">
                <label className="block text-sm font-medium text-muted-foreground mb-1.5">Placeholder</label>
                <Input value={config.placeholder} onChange={e => handleChange('placeholder', e.target.value)} placeholder="Type your message..." />
              </div>
            </div>
          </Section>

          {/* Colors */}
          <Section title="Colors" icon={<Palette className="h-4 w-4 text-muted-foreground" />}>
            <div className="space-y-5">
              <ColorField label="Primary Color" value={config.primaryColor} onChange={v => handleChange('primaryColor', v)} />
              <div>
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">User Messages</p>
                <div className="grid grid-cols-2 gap-4">
                  <ColorField label="Background" value={config.userMessageBg} onChange={v => handleChange('userMessageBg', v)} />
                  <ColorField label="Text" value={config.userMessageText} onChange={v => handleChange('userMessageText', v)} />
                </div>
              </div>
              <div>
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">Bot Messages</p>
                <div className="grid grid-cols-2 gap-4">
                  <ColorField label="Background" value={config.botMessageBg} onChange={v => handleChange('botMessageBg', v)} />
                  <ColorField label="Text" value={config.botMessageText} onChange={v => handleChange('botMessageText', v)} />
                </div>
              </div>
              <div>
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">Agent Messages</p>
                <div className="grid grid-cols-2 gap-4">
                  <ColorField label="Background" value={config.agentMessageBg} onChange={v => handleChange('agentMessageBg', v)} />
                  <ColorField label="Text" value={config.agentMessageText} onChange={v => handleChange('agentMessageText', v)} />
                </div>
              </div>
            </div>
          </Section>

          {/* Typography */}
          <Section title="Typography" icon={<Type className="h-4 w-4 text-muted-foreground" />}>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="col-span-2">
                <label className="block text-sm font-medium text-muted-foreground mb-1.5">Font Family</label>
                <NativeSelect value={config.fontFamily} onValueChange={v => handleChange('fontFamily', v)}>
                  <optgroup label="Sans-serif">
                    <option value="Inter, system-ui, sans-serif">Inter (Default)</option>
                    <option value="DM Sans, system-ui, sans-serif">DM Sans</option>
                    <option value="Plus Jakarta Sans, system-ui, sans-serif">Plus Jakarta Sans</option>
                    <option value="Outfit, system-ui, sans-serif">Outfit</option>
                    <option value="Nunito, system-ui, sans-serif">Nunito</option>
                    <option value="Poppins, system-ui, sans-serif">Poppins</option>
                    <option value="Lato, system-ui, sans-serif">Lato</option>
                    <option value="Rubik, system-ui, sans-serif">Rubik</option>
                    <option value="Source Sans 3, system-ui, sans-serif">Source Sans 3</option>
                    <option value="Manrope, system-ui, sans-serif">Manrope</option>
                    <option value="Space Grotesk, system-ui, sans-serif">Space Grotesk</option>
                  </optgroup>
                  <optgroup label="Serif">
                    <option value="Merriweather, Georgia, serif">Merriweather</option>
                    <option value="Lora, Georgia, serif">Lora</option>
                    <option value="Playfair Display, Georgia, serif">Playfair Display</option>
                  </optgroup>
                  <optgroup label="Monospace">
                    <option value="JetBrains Mono, monospace">JetBrains Mono</option>
                    <option value="Fira Code, monospace">Fira Code</option>
                  </optgroup>
                  <optgroup label="System">
                    <option value="-apple-system, BlinkMacSystemFont, Segoe UI, system-ui, sans-serif">System Default</option>
                  </optgroup>
                </NativeSelect>
              </div>
              <div>
                <label className="block text-sm font-medium text-muted-foreground mb-1.5">Font Size (px)</label>
                <Input type="number" min={10} max={20} value={config.fontSize} onChange={e => handleChange('fontSize', parseInt(e.target.value) || 14)} />
              </div>
              <div>
                <label className="block text-sm font-medium text-muted-foreground mb-1.5">Font Weight</label>
                <NativeSelect value={String(config.fontWeight)} onValueChange={v => handleChange('fontWeight', parseInt(v))}>
                  <option value="300">Light (300)</option>
                  <option value="400">Regular (400)</option>
                  <option value="450">Medium (450)</option>
                  <option value="500">Medium (500)</option>
                  <option value="600">Semi-Bold (600)</option>
                  <option value="700">Bold (700)</option>
                </NativeSelect>
              </div>
            </div>
          </Section>

          {/* Widget Icon */}
          <Section title="Widget Icon" icon={<Image className="h-4 w-4 text-muted-foreground" />}>
            <div className="space-y-4">
              {/* Type selector */}
              <div className="flex gap-2">
                {(['emoji', 'library', 'upload'] as const).map(type => (
                  <button
                    key={type}
                    type="button"
                    onClick={() => handleChange('iconType', type)}
                    className={cn(
                      'flex items-center gap-1.5 px-3 py-1.5 rounded-md border text-xs font-medium transition-colors',
                      config.iconType === type
                        ? 'bg-primary/10 border-primary/30 text-primary'
                        : 'bg-background border-border text-muted-foreground hover:bg-muted'
                    )}
                  >
                    {type === 'emoji' && '😀 Emoji'}
                    {type === 'library' && '👥 Avatars'}
                    {type === 'upload' && '📁 Upload'}
                  </button>
                ))}
              </div>

              {/* Emoji grid */}
              {config.iconType === 'emoji' && (
                <div className="grid grid-cols-8 gap-1.5 p-3 border rounded-lg bg-muted/30 max-h-40 overflow-y-auto">
                  {[
                    '😊', '😎', '🤓', '😇', '🥳', '😍', '🤗', '😋',
                    '🙂', '😌', '😴', '🤔', '😏', '🤭', '😄', '😆',
                    '🐶', '🐱', '🐭', '🐹', '🦊', '🐻', '🐼', '🐨',
                    '🐯', '🦁', '🐸', '🐵', '🦄', '🐺', '🦝', '🐾',
                    '🍎', '🍕', '🍔', '🍰', '☕', '🍵', '🎂', '🍪',
                    '🎭', '🎨', '🎵', '⚽', '🏀', '🎯', '🎮', '📚',
                  ].map((emoji, i) => (
                    <button
                      key={i}
                      type="button"
                      onClick={() => setConfig(prev => ({ ...prev, widgetIcon: emoji, iconType: 'emoji' }))}
                      className={cn(
                        'w-9 h-9 flex items-center justify-center text-lg rounded-md border transition-colors hover:bg-primary/10',
                        config.widgetIcon === emoji ? 'bg-primary/10 border-primary/30' : 'bg-background border-border'
                      )}
                    >
                      {emoji}
                    </button>
                  ))}
                </div>
              )}

              {/* Avatar library */}
              {config.iconType === 'library' && (
                <div className="space-y-2">
                  <div className="flex items-center gap-1 text-xs text-primary">
                    <Sparkles className="w-3 h-3" />
                    Custom Characters
                  </div>
                  <div className="grid grid-cols-6 gap-2 p-3 border rounded-lg bg-muted/30 max-h-56 overflow-y-auto">
                    {Array.from({ length: 48 }, (_, i) => {
                      const n = i + 1
                      const url = `${config.apiUrl}/static/avatars/peep-${n}.svg`
                      return (
                        <button
                          key={n}
                          type="button"
                          onClick={() => setConfig(prev => ({ ...prev, widgetIcon: url, iconType: 'library' }))}
                          className={cn(
                            'flex flex-col items-center gap-0.5 p-2 rounded-lg border transition-all hover:bg-primary/10 hover:scale-105',
                            config.widgetIcon === url ? 'bg-primary/10 border-primary/30' : 'bg-background border-border'
                          )}
                        >
                          <img
                            src={url}
                            alt={`Character ${n}`}
                            className="w-7 h-7 object-contain"
                            onError={e => { (e.target as HTMLImageElement).src = 'data:image/svg+xml,<svg viewBox="0 0 24 24" fill="currentColor"><text y="18" font-size="20">🤖</text></svg>' }}
                          />
                          <span className="text-[10px] text-muted-foreground">#{n}</span>
                        </button>
                      )
                    })}
                  </div>
                </div>
              )}

              {/* Upload */}
              {config.iconType === 'upload' && (
                <div className="border-2 border-dashed border-border rounded-lg p-6 text-center hover:border-muted-foreground/50 transition-colors">
                  <div className="space-y-2">
                    <div className="mx-auto w-10 h-10 text-muted-foreground">
                      <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" className="w-full h-full">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                      </svg>
                    </div>
                    <label htmlFor="widget-icon-upload-page" className="cursor-pointer text-sm">
                      <span className="text-primary hover:underline font-medium">Upload a file</span>
                      <span className="text-muted-foreground"> or drag and drop</span>
                    </label>
                    <input
                      id="widget-icon-upload-page"
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={e => {
                        const file = e.target.files?.[0]
                        if (file) {
                          const reader = new FileReader()
                          reader.onload = ev => setConfig(prev => ({ ...prev, widgetIcon: ev.target?.result as string, iconType: 'upload' }))
                          reader.readAsDataURL(file)
                        }
                      }}
                    />
                    <p className="text-xs text-muted-foreground">PNG, JPG, SVG up to 1MB</p>
                  </div>
                </div>
              )}

              {/* Current icon preview */}
              {config.widgetIcon && (
                <div className="flex items-center gap-3 p-3 bg-muted/30 rounded-lg">
                  <div className="w-10 h-10 flex items-center justify-center bg-background rounded-full border shadow-sm">
                    {config.iconType === 'emoji' ? (
                      <span className="text-xl">{config.widgetIcon}</span>
                    ) : (
                      <img
                        src={config.widgetIcon}
                        alt="Widget icon"
                        className="w-8 h-8 rounded-full object-cover"
                        onError={e => { (e.target as HTMLImageElement).src = 'data:image/svg+xml,<svg viewBox="0 0 24 24" fill="currentColor"><text y="18" font-size="20">🤖</text></svg>' }}
                      />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium">Widget Icon Set</p>
                    <p className="text-xs text-muted-foreground">Appears in chat header and bot messages</p>
                  </div>
                  <Button variant="ghost" size="sm" className="text-destructive hover:text-destructive" onClick={() => handleChange('widgetIcon', '')}>
                    Remove
                  </Button>
                </div>
              )}
            </div>
          </Section>

          {/* Style */}
          <Section title="Style" icon={<Eye className="h-4 w-4 text-muted-foreground" />} defaultOpen={false}>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-muted-foreground mb-1.5">Border Radius (px)</label>
                <Input type="number" min={0} max={50} value={config.borderRadius} onChange={e => handleChange('borderRadius', parseInt(e.target.value) || 0)} />
              </div>
              <div>
                <label className="block text-sm font-medium text-muted-foreground mb-1.5">Shadow</label>
                <NativeSelect value={config.shadowIntensity} onValueChange={v => handleChange('shadowIntensity', v)}>
                  <option value="none">None</option>
                  <option value="light">Light</option>
                  <option value="medium">Medium</option>
                  <option value="strong">Strong</option>
                </NativeSelect>
              </div>
              <div>
                <label className="block text-sm font-medium text-muted-foreground mb-1.5">Animation Speed</label>
                <NativeSelect value={config.animationSpeed} onValueChange={v => handleChange('animationSpeed', v)}>
                  <option value="slow">Slow</option>
                  <option value="normal">Normal</option>
                  <option value="fast">Fast</option>
                </NativeSelect>
              </div>
              <div>
                <label className="block text-sm font-medium text-muted-foreground mb-1.5">Message Style</label>
                <NativeSelect value={config.messageStyle} onValueChange={v => handleChange('messageStyle', v)}>
                  <option value="rounded">Rounded (Modern)</option>
                  <option value="sharp">Sharp (Corporate)</option>
                  <option value="bubble">Bubble (Friendly)</option>
                </NativeSelect>
              </div>
              <div>
                <label className="block text-sm font-medium text-muted-foreground mb-1.5">Widget Width (px)</label>
                <Input
                  type="number"
                  min={360}
                  max={600}
                  value={config.widgetWidth}
                  onChange={e => handleChange('widgetWidth', Math.min(600, Math.max(360, parseInt(e.target.value) || 440)))}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-muted-foreground mb-1.5">Widget Height (px)</label>
                <Input
                  type="number"
                  min={500}
                  max={900}
                  value={config.widgetHeight}
                  onChange={e => handleChange('widgetHeight', Math.min(900, Math.max(500, parseInt(e.target.value) || 720)))}
                />
              </div>
            </div>
          </Section>

          {/* Advanced */}
          <Section title="Advanced" icon={<Code className="h-4 w-4 text-muted-foreground" />} defaultOpen={false}>
            <div className="space-y-4">
              {/* Custom CSS */}
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="text-sm font-medium text-muted-foreground">Custom CSS</label>
                  <button
                    type="button"
                    onClick={() => setShowCSSGuide(true)}
                    className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
                  >
                    <HelpCircle className="w-3 h-3" />
                    CSS Guide
                  </button>
                </div>
                <Textarea
                  value={config.customCSS}
                  onChange={e => handleChange('customCSS', e.target.value)}
                  placeholder={`/* Override widget styles (Shadow DOM) */\n.w-window { /* chat window */ }\n.w-toggle { /* floating button */ }`}
                  rows={4}
                  className="font-mono text-xs"
                />
              </div>

              {/* Z-Index */}
              <div>
                <label className="block text-sm font-medium text-muted-foreground mb-1.5">Z-Index</label>
                <Input type="number" value={config.zIndex} onChange={e => handleChange('zIndex', parseInt(e.target.value))} />
              </div>

              {/* Session Mode */}
              <div>
                <label className="block text-sm font-medium text-muted-foreground mb-1.5">Session Mode</label>
                <NativeSelect value={config.sessionMode} onValueChange={v => handleChange('sessionMode', v)}>
                  <option value="continuous">Continuous (same chat across pages)</option>
                  <option value="per_page">Per Page (new chat per page)</option>
                </NativeSelect>
                <p className="mt-1 text-xs text-muted-foreground">
                  Continuous keeps one conversation across all pages. Per Page starts a separate conversation on each page.
                </p>
              </div>

              {/* Show Branding toggle */}
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium">Show Branding</p>
                  <p className="text-xs text-muted-foreground">"Powered by CleverAct AI" footer</p>
                </div>
                <Switch checked={config.showBranding} onCheckedChange={v => handleChange('showBranding', v)} />
              </div>
            </div>
          </Section>
        </div>

        {/* ── RIGHT: Preview + Integration Code ── */}
        <div className="space-y-4">
          {/* Live preview */}
          <div className="sticky top-4">
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm flex items-center gap-1.5">
                    <Eye className="h-4 w-4" />
                    Live Preview
                  </CardTitle>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="gap-1.5 text-xs"
                    onClick={() => window.open(`/widget-demo?chatbot=${chatbotId}`, '_blank', 'noopener,noreferrer')}
                  >
                    <ExternalLink className="w-3 h-3" />
                    Test Live
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="pt-0">
                {/* Pixel-perfect replica of premium-widget-v4 */}
                {(() => {
                  const isDark = config.theme === 'dark'
                  const bg = isDark ? '#1e1e2e' : '#ffffff'
                  const bgSecondary = isDark ? '#2a2a3e' : '#f8f9fb'
                  const bgChat = isDark ? '#16162a' : '#fafbfc'
                  const border = isDark ? '#333352' : '#e8ecf0'
                  const text = isDark ? '#e2e8f0' : '#111827'
                  const textSecondary = isDark ? '#a0aec0' : '#4b5563'
                  const textMuted = isDark ? '#94a3b8' : '#9ca3af'
                  const radius = Math.min(config.borderRadius, 20)
                  const shadow = config.shadowIntensity === 'none' ? 'none'
                    : config.shadowIntensity === 'light' ? '0 2px 8px rgba(0,0,0,0.08)'
                    : config.shadowIntensity === 'strong' ? '0 8px 32px rgba(0,0,0,0.18), 0 2px 8px rgba(0,0,0,0.06)'
                    : '0 4px 16px rgba(0,0,0,0.12), 0 2px 8px rgba(0,0,0,0.04)'
                  // Derive primary tints
                  const primaryUltraLight = isDark ? 'rgba(255,255,255,0.08)' : `${config.primaryColor}12`

                  // Avatar renderer — matches widget exactly
                  const renderAvatar = (size: number, radiusPx: number) => {
                    if (config.widgetIcon && config.iconType === 'emoji') {
                      return (
                        <div style={{ width: size, height: size, borderRadius: radiusPx, background: primaryUltraLight, border: `1px solid ${config.primaryColor}20`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                          <span style={{ fontSize: size * 0.55, lineHeight: 1 }}>{config.widgetIcon}</span>
                        </div>
                      )
                    }
                    if (config.widgetIcon && (config.iconType === 'library' || config.iconType === 'upload')) {
                      return (
                        <div style={{ width: size, height: size, borderRadius: radiusPx, background: primaryUltraLight, border: `1px solid ${config.primaryColor}20`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, overflow: 'hidden' }}>
                          <img key={config.widgetIcon} src={config.widgetIcon} alt="" style={{ width: size * 0.75, height: size * 0.75, objectFit: 'cover', borderRadius: radiusPx * 0.6 }} onError={e => { (e.target as HTMLImageElement).style.display = 'none' }} />
                        </div>
                      )
                    }
                    // Default chat icon
                    return (
                      <div style={{ width: size, height: size, borderRadius: radiusPx, background: primaryUltraLight, border: `1px solid ${config.primaryColor}20`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <svg width={size * 0.42} height={size * 0.42} viewBox="0 0 24 24" fill="none" stroke={config.primaryColor} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                        </svg>
                      </div>
                    )
                  }

                  // Message bubble border-radius per style
                  const botBubbleRadius = config.messageStyle === 'sharp' ? '4px 20px 20px 20px'
                    : config.messageStyle === 'bubble' ? '4px 18px 18px 18px'
                    : '4px 20px 20px 20px'
                  const userBubbleRadius = config.messageStyle === 'sharp' ? '20px 20px 4px 20px'
                    : config.messageStyle === 'bubble' ? '18px 18px 4px 18px'
                    : '20px 20px 4px 20px'

                  const now = new Date()
                  const ts = now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })

                  return (
                    <div style={{ position: 'relative' }}>
                      {/* Chat window */}
                      <div
                        style={{
                          fontFamily: config.fontFamily,
                          fontSize: config.fontSize,
                          fontWeight: config.fontWeight,
                          borderRadius: radius,
                          boxShadow: shadow,
                          border: `1px solid ${border}`,
                          background: bg,
                          display: 'flex',
                          flexDirection: 'column',
                          overflow: 'hidden',
                          maxHeight: 540,
                        }}
                      >
                        {/* ── Header ── */}
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            padding: '16px 12px 16px 18px',
                            background: bg,
                            borderBottom: `1px solid ${border}`,
                            flexShrink: 0,
                          }}
                        >
                          {/* Avatar + info */}
                          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1, minWidth: 0 }}>
                            {renderAvatar(38, 12)}
                            <div style={{ minWidth: 0 }}>
                              <div style={{ fontSize: 14, fontWeight: 600, color: text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {config.widgetTitle}
                              </div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11.5, color: textMuted, marginTop: 1 }}>
                                <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#22c55e', animation: 'pulse 2s ease-in-out infinite' }} />
                                Online
                              </div>
                            </div>
                          </div>
                          {/* Action buttons */}
                          <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            {/* Menu */}
                            <div style={{ width: 34, height: 34, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', color: textMuted, cursor: 'pointer' }}>
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><circle cx="12" cy="5" r="1.5"/><circle cx="12" cy="12" r="1.5"/><circle cx="12" cy="19" r="1.5"/></svg>
                            </div>
                            {/* Expand */}
                            <div style={{ width: 34, height: 34, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', color: textMuted, cursor: 'pointer' }}>
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 3 21 3 21 9"/><polyline points="9 21 3 21 3 15"/><line x1="21" y1="3" x2="14" y2="10"/><line x1="3" y1="21" x2="10" y2="14"/></svg>
                            </div>
                            {/* Minimize */}
                            <div style={{ width: 34, height: 34, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', color: textMuted, cursor: 'pointer' }}>
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"/></svg>
                            </div>
                          </div>
                        </div>

                        {/* ── Messages ── */}
                        <div
                          style={{
                            flex: 1,
                            overflowY: 'auto',
                            padding: '20px 18px 12px',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 2,
                            background: bgChat,
                            minHeight: 260,
                          }}
                        >
                          {/* Bot greeting */}
                          <div style={{ display: 'flex', gap: 10, marginBottom: 4 }}>
                            {renderAvatar(30, 10)}
                            <div style={{ display: 'flex', flexDirection: 'column', maxWidth: '80%' }}>
                              <div style={{
                                padding: '10px 14px',
                                background: config.botMessageBg,
                                color: config.botMessageText,
                                borderRadius: botBubbleRadius,
                                fontSize: config.fontSize,
                                fontWeight: config.fontWeight,
                                lineHeight: 1.55,
                                width: 'fit-content',
                                border: `1px solid ${border}`,
                                wordBreak: 'break-word',
                              }}>
                                {config.greeting}
                              </div>
                              <div style={{ fontSize: 10.5, color: textMuted, padding: '4px 2px 0' }}>{ts}</div>
                            </div>
                          </div>

                          {/* User message */}
                          <div style={{ display: 'flex', flexDirection: 'row-reverse', marginBottom: 4 }}>
                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', maxWidth: '80%' }}>
                              <div style={{
                                padding: '10px 14px',
                                background: config.userMessageBg,
                                color: config.userMessageText,
                                borderRadius: userBubbleRadius,
                                fontSize: config.fontSize,
                                fontWeight: config.fontWeight,
                                lineHeight: 1.55,
                                width: 'fit-content',
                                wordBreak: 'break-word',
                              }}>
                                hello
                              </div>
                              <div style={{ fontSize: 10.5, color: textMuted, padding: '4px 2px 0' }}>{ts}</div>
                            </div>
                          </div>

                          {/* Bot reply */}
                          <div style={{ display: 'flex', gap: 10, marginBottom: 4 }}>
                            {renderAvatar(30, 10)}
                            <div style={{ display: 'flex', flexDirection: 'column', maxWidth: '80%' }}>
                              <div style={{
                                padding: '10px 14px',
                                background: config.botMessageBg,
                                color: config.botMessageText,
                                borderRadius: botBubbleRadius,
                                fontSize: config.fontSize,
                                fontWeight: config.fontWeight,
                                lineHeight: 1.55,
                                width: 'fit-content',
                                border: `1px solid ${border}`,
                                wordBreak: 'break-word',
                              }}>
                                Of course! I'd be happy to help you. What would you like to know?
                              </div>
                              <div style={{ fontSize: 10.5, color: textMuted, padding: '4px 2px 0' }}>{ts}</div>
                            </div>
                          </div>

                          {/* Agent joined banner */}
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 0 4px' }}>
                            <div style={{ flex: 1, height: 1, background: border }} />
                            <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#10b981' }} />
                            <span style={{ fontSize: 11.5, color: textMuted, whiteSpace: 'nowrap' }}>Agent joined</span>
                            <div style={{ flex: 1, height: 1, background: border }} />
                          </div>

                          {/* Agent message */}
                          <div style={{ display: 'flex', gap: 10, marginBottom: 4 }}>
                            <div style={{ width: 30, height: 30, borderRadius: 10, background: isDark ? '#1a3a2a' : '#ecfdf5', border: '1px solid #d1fae5', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                              <span style={{ fontSize: 12, fontWeight: 700, color: '#059669' }}>A</span>
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', maxWidth: '80%' }}>
                              <div style={{
                                padding: '10px 14px',
                                background: config.agentMessageBg,
                                color: config.agentMessageText,
                                borderRadius: botBubbleRadius,
                                fontSize: config.fontSize,
                                fontWeight: config.fontWeight,
                                lineHeight: 1.55,
                                width: 'fit-content',
                                border: `1px solid ${isDark ? '#1a3a2a' : '#d1fae5'}`,
                                wordBreak: 'break-word',
                              }}>
                                Hi there! I'm a human agent. How can I assist you?
                              </div>
                              <div style={{ display: 'flex', gap: 6, fontSize: 10.5, color: textMuted, padding: '4px 2px 0' }}>
                                <span>{ts}</span>
                                <span style={{ color: '#059669', fontWeight: 600, fontSize: 11 }}>{'•'} Agent</span>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* ── Input area ── */}
                        <div style={{ padding: '12px 14px 14px', borderTop: `1px solid ${border}`, background: bg, flexShrink: 0 }}>
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'flex-end',
                              gap: 8,
                              background: bgSecondary,
                              border: `1.5px solid ${border}`,
                              borderRadius: 14,
                              padding: '4px 5px 4px 16px',
                            }}
                          >
                            <div style={{ flex: 1, padding: '7px 0', color: textMuted, fontSize: 14, lineHeight: 1.5, fontFamily: config.fontFamily }}>
                              {config.placeholder}
                            </div>
                            {/* Send button */}
                            <div
                              style={{
                                width: 34,
                                height: 34,
                                borderRadius: 10,
                                background: config.primaryColor,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0,
                                opacity: 0.35,
                              }}
                            >
                              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <line x1="22" y1="2" x2="11" y2="13" />
                                <polygon points="22 2 15 22 11 13 2 9 22 2" />
                              </svg>
                            </div>
                          </div>
                        </div>

                        {/* ── Branding ── */}
                        {config.showBranding && (
                          <div style={{ textAlign: 'center', padding: '4px 0 8px', fontSize: 10, color: textMuted, background: bg, letterSpacing: '0.01em' }}>
                            Powered by CleverAct AI
                          </div>
                        )}
                      </div>

                    </div>
                  )
                })()}
              </CardContent>
            </Card>

            {/* Integration Code — collapsible */}
            <div className="mt-3">
              <Collapsible open={showCode} onOpenChange={setShowCode}>
                <Card>
                  <CollapsibleTrigger asChild>
                    <button className="flex items-center justify-between w-full px-4 py-3 text-left hover:bg-muted/50 transition-colors rounded-t-lg">
                      <div className="flex items-center gap-2">
                        <Code className="h-4 w-4 text-muted-foreground" />
                        <span className="text-sm font-semibold">Integration Code</span>
                      </div>
                      {showCode ? <ChevronDown className="h-4 w-4 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 text-muted-foreground" />}
                    </button>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <CardContent className="pt-0 pb-4 px-4">
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-xs text-muted-foreground">
                          Paste before the closing &lt;/body&gt; tag
                        </p>
                        <Button variant="outline" size="sm" className="gap-1.5 text-xs h-7" onClick={copyEmbedCode}>
                          {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                          {copied ? 'Copied!' : 'Copy'}
                        </Button>
                      </div>
                      <div className="bg-zinc-900 text-emerald-400 p-3 rounded-md font-mono text-[11px] overflow-x-auto max-h-48 overflow-y-auto">
                        <pre>{generateEmbedCode()}</pre>
                      </div>

                      {/* Variables info */}
                      {(() => {
                        const variables = (chatbot as any)?.settings?.variables || {}
                        const varEntries = Object.entries(variables)
                        if (varEntries.length === 0) return null
                        return (
                          <div className="mt-3 p-3 bg-amber-500/10 border border-amber-500/20 rounded-md">
                            <p className="text-xs font-medium text-amber-700 dark:text-amber-400 mb-2">Variables for this bot:</p>
                            <div className="space-y-1">
                              {varEntries.map(([name, cfg]: [string, any]) => (
                                <div key={name} className="flex items-center gap-1.5 text-xs">
                                  <code className="bg-amber-500/10 text-amber-800 dark:text-amber-300 px-1 py-0.5 rounded font-mono">{name}</code>
                                  {cfg?.required && <span className="text-[10px] bg-destructive/10 text-destructive px-1 py-0.5 rounded font-medium">required</span>}
                                  {cfg?.identity && <span className="text-[10px] bg-primary/10 text-primary px-1 py-0.5 rounded font-medium">identity</span>}
                                </div>
                              ))}
                            </div>
                          </div>
                        )
                      })()}
                    </CardContent>
                  </CollapsibleContent>
                </Card>
              </Collapsible>
            </div>
          </div>
        </div>
      </div>

      {/* ── CSS Guide Modal ── */}
      <Modal
        isOpen={showCSSGuide}
        onClose={() => setShowCSSGuide(false)}
        title="Custom CSS Guide"
        description="Copy any example below and paste it into the Custom CSS field."
        size="lg"
      >
        <div className="overflow-y-auto pb-8 space-y-6">
          <div>
            <h4 className="text-sm font-semibold mb-3">Available Selectors</h4>
            <div className="grid grid-cols-2 gap-2 text-sm">
              {[
                ['.w-toggle', 'Floating chat button'],
                ['.w-window', 'Chat window container'],
                ['.w-header', 'Header bar'],
                ['.w-messages', 'Messages scroll area'],
                ['.w-msg', 'Individual message row'],
                ['.w-msg.user', 'User message'],
                ['.w-msg.bot', 'Bot message'],
                ['.w-bubble', 'Message bubble'],
                ['.w-input-wrap', 'Input area wrapper'],
                ['.w-input-wrap textarea', 'Text input field'],
                ['.w-send-btn', 'Send button'],
                ['.w-header-title', 'Header title text'],
                ['.w-header-avatar', 'Header avatar'],
                ['.w-badge', 'Unread badge on toggle'],
              ].map(([sel, desc]) => (
                <div key={sel} className="flex items-baseline gap-2 py-1">
                  <code className="bg-muted text-primary px-1.5 py-0.5 rounded text-xs font-mono shrink-0">{sel}</code>
                  <span className="text-muted-foreground text-xs">{desc}</span>
                </div>
              ))}
            </div>
          </div>
          <hr className="border-border" />
          <div className="space-y-4">
            <h4 className="text-sm font-semibold">Examples</h4>
            {[
              { title: 'Rounded toggle button', css: `.w-toggle {\n  border-radius: 50% !important;\n}` },
              { title: 'Wider chat window', css: `.w-window {\n  width: 500px !important;\n  max-height: 800px !important;\n}` },
              { title: 'Custom header background', css: `.w-header {\n  background: linear-gradient(135deg, #667eea, #764ba2) !important;\n}` },
              { title: 'Hide header avatar', css: `.w-header-avatar {\n  display: none !important;\n}` },
              { title: 'Larger message text', css: `.w-bubble {\n  font-size: 16px !important;\n  line-height: 1.6 !important;\n}` },
              { title: 'Square message bubbles', css: `.w-bubble {\n  border-radius: 4px !important;\n}` },
              { title: 'Taller input area', css: `.w-input-wrap textarea {\n  min-height: 60px !important;\n}` },
              { title: 'Hide branding footer', css: `.w-branding {\n  display: none !important;\n}` },
            ].map(example => (
              <div key={example.title} className="border rounded-lg overflow-hidden">
                <div className="flex items-center justify-between bg-muted/50 px-3 py-2 border-b">
                  <span className="text-sm font-medium">{example.title}</span>
                  <button
                    type="button"
                    onClick={() => navigator.clipboard.writeText(example.css)}
                    className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
                  >
                    <Copy className="w-3 h-3" />
                    Copy
                  </button>
                </div>
                <pre className="px-3 py-2 text-sm font-mono bg-background whitespace-pre overflow-x-auto">{example.css}</pre>
              </div>
            ))}
          </div>
          <hr className="border-border" />
          <div className="bg-primary/5 border border-primary/10 rounded-lg p-4">
            <h4 className="text-sm font-semibold mb-2">Tips</h4>
            <ul className="text-sm text-muted-foreground space-y-1 list-disc list-inside">
              <li>Use <code className="bg-muted px-1 rounded text-xs">!important</code> to override default styles</li>
              <li>CSS variables like <code className="bg-muted px-1 rounded text-xs">var(--w-primary)</code> reference your configured colors</li>
              <li>The widget uses Shadow DOM so your page CSS won't affect it</li>
              <li>Changes apply after saving and refreshing the widget</li>
            </ul>
          </div>
        </div>
      </Modal>
    </div>
  )
}
